import { useCallback, useEffect, useState } from 'react';
import {
	Promotion,
	trackSportsbookPromotionCarouselClick,
	trackSportsbookPromotionCarouselImpression
} from '@comeon/analytics';

import useTranslations from '~/components/translation/use-translations';
import { isSmallerSelector } from '~/components/layout/mediaquery';

import { OddsFormat } from '../../sportsbook-types';
import { RecommendedBetDto, FeaturedContentTypeDto } from '~/components/sportsbook/lobby/api/sbapi/sbapi-api-model';
import SportsbookCarousel from '../carousel/sportsbook-carousel';
import { BET_RECOMMENDATIONS_TEXT_KEYS } from './bet-recommendations-bet/sportsbook-bet-recommendations-bet-text-keys';

import BetRecommendationsBetSkeleton from './bet-recommendations-bet/sportsbook-bet-recommendations-bet-skeleton';
import BetRecommendationsBet from './bet-recommendations-bet/sportsbook-bet-recommendations-bet';
import BetRecommendationsBetWrapper from './bet-recommendations-bet/sportsbook-bet-recommendations-bet-wrapper';
import { useAppSelector } from '~/store/types';

const CAROUSEL_NAME = 'PreCanned Carousel';

const skeletons = Array(3)
	.fill(0)
	.map((_, i) => `pre-canned-skeleton-key-${i}`);

const mapBetToPromoItem = (recommendedBet: RecommendedBetDto) => {
	return {
		id: recommendedBet.items.map(item => item.event.id)[0],
		type: FeaturedContentTypeDto.PRE_CANNED,
		sportId: recommendedBet.items.map(item => item.event.sportId)[0],
		selectionIds: recommendedBet.items.map(item => item.selection.externalId),
		marketIds: recommendedBet.items.map(item => item.market.externalId)
	} as Promotion;
};

type SportsbookPreCannedRecommendationsCarouselProps = {
	recommendedBets: RecommendedBetDto[];
	isLoading: boolean;
	oddsFormat: OddsFormat;
	onBetClick: (recommendedBet: RecommendedBetDto) => void;
	activeSelectionIds?: string[];
	showArrows?: boolean;
	paddingX?: number;
};

const SportsbookPreCannedRecommendationsCarousel: React.FC<SportsbookPreCannedRecommendationsCarouselProps> = ({
	recommendedBets,
	isLoading,
	oddsFormat,
	onBetClick,
	activeSelectionIds = [],
	showArrows = false,
	paddingX = 0
}) => {
	const { translations, isLoadingTranslations } = useTranslations(BET_RECOMMENDATIONS_TEXT_KEYS);
	const isSmall = useAppSelector(isSmallerSelector);

	const [isImpressionEventLoaded, setIsImpressionEventLoaded] = useState(false);

	// Track impression
	useEffect(() => {
		if (!recommendedBets || recommendedBets.length === 0 || isLoading || isImpressionEventLoaded) {
			return;
		}
		trackSportsbookPromotionCarouselImpression(recommendedBets.map(mapBetToPromoItem), CAROUSEL_NAME);
		setIsImpressionEventLoaded(true);
	}, [isImpressionEventLoaded, isLoading, recommendedBets]);

	// Track click and propagate
	const handleCarouselItemClick = useCallback(
		(index: number) => {
			const recommendedBet = recommendedBets && recommendedBets[index];
			if (recommendedBet) {
				trackSportsbookPromotionCarouselClick(mapBetToPromoItem(recommendedBet), CAROUSEL_NAME, index);
			}
			onBetClick(recommendedBet);
		},
		[recommendedBets, onBetClick]
	);

	if (!isLoading && recommendedBets?.length === 0) {
		return null;
	}

	if (isLoadingTranslations || isLoading) {
		return (
			<SportsbookCarousel showArrows={false} gap={isSmall ? 12 : 20} paddingX={paddingX}>
				{skeletons.map(s => (
					<BetRecommendationsBetSkeleton key={s} />
				))}
			</SportsbookCarousel>
		);
	}

	return (
		<SportsbookCarousel showArrows={showArrows} gap={isSmall ? 12 : 20} paddingX={paddingX}>
			{recommendedBets?.flatMap((bet, index) => {
				const isActive =
					activeSelectionIds.length > 0 &&
					bet.items.every(item => activeSelectionIds.includes(item.selection.id));

				return (
					<BetRecommendationsBetWrapper key={index}>
						<BetRecommendationsBet
							key={index}
							bet={bet}
							isActive={isActive}
							translations={translations}
							oddsFormat={oddsFormat}
							onClick={handleCarouselItemClick?.bind(this, index)}
						/>
					</BetRecommendationsBetWrapper>
				);
			})}
		</SportsbookCarousel>
	);
};

export default SportsbookPreCannedRecommendationsCarousel;
